import { Component, EventEmitter, Input, OnInit, Output, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgxMapboxGLModule } from 'ngx-mapbox-gl';
import { Subject, debounceTime, distinctUntilChanged, switchMap } from 'rxjs';
import { PickpointStationService } from '../../../_services/pickpoint-station.service';
import { PickpointStation } from '../../../_models/shipping-config.interface';

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, NgxMapboxGLModule],
  selector: 'hawk-pickpoint-map-picker',
  templateUrl: './pickpoint-map-picker.component.html',
  styles: [`
    .marker-center {
      width: 18px; height: 18px; border-radius: 50%;
      background: #af0000; border: 2px solid white; box-shadow: 0 0 0 2px #af0000;
    }
    .marker-pp {
      max-width: 260px;
      padding: 4px 10px;
      border-radius: 14px;
      background: #1d4ed8; color: white;
      font-size: 11px; font-weight: 600;
      border: 2px solid white;
      box-shadow: 0 1px 2px rgba(0,0,0,0.4);
      cursor: pointer;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .marker-pp.selected {
      background: #059669;
      transform: scale(1.08);
      z-index: 2;
    }
  `],
})
export class PickpointMapPickerComponent implements OnInit {
  @Input({ required: true }) centerLat!: number;
  @Input({ required: true }) centerLng!: number;
  @Input() referenceDate?: string;
  @Input() initialStationId?: number;

  @Output() stationSelected = new EventEmitter<PickpointStation | null>();
  @Output() closed = new EventEmitter<void>();

  radiusKm = 10;
  currentRadiusKm = signal(10);
  stations = signal<PickpointStation[]>([]);
  selected = signal<PickpointStation | null>(null);
  loading = signal(false);

  radiusCircle = computed<GeoJSON.Feature<GeoJSON.Polygon>>(() =>
    this.buildCirclePolygon(this.centerLat, this.centerLng, this.currentRadiusKm())
  );

  private radiusSubject = new Subject<number>();

  constructor(private pickpointService: PickpointStationService) {}

  ngOnInit() {
    this.radiusSubject.pipe(
      debounceTime(350),
      distinctUntilChanged(),
      switchMap(radius => {
        this.loading.set(true);
        return this.pickpointService.getNearbyStations(
          this.centerLat, this.centerLng, radius, this.referenceDate
        );
      })
    ).subscribe(results => {
      this.stations.set(results);
      if (this.initialStationId) {
        const match = results.find(s => s.id === this.initialStationId);
        if (match) this.selected.set(match);
      }
      this.loading.set(false);
    });

    this.radiusSubject.next(this.radiusKm);
  }

  onRadiusChange(value: number) {
    const v = Number(value);
    if (!Number.isFinite(v) || v <= 0) return;
    this.currentRadiusKm.set(v);
    this.radiusSubject.next(v);
  }

  // Approximiert einen geografischen Kreis als Polygon mit 64 Stuetzpunkten.
  // Laengen-Offset wird anhand der Breite skaliert (cos), damit der Kreis
  // nicht zum Oval wird.
  private buildCirclePolygon(lat: number, lng: number, radiusKm: number): GeoJSON.Feature<GeoJSON.Polygon> {
    const points = 64;
    const latDegPerKm = 1 / 111.32;
    const lngDegPerKm = 1 / (111.32 * Math.cos(lat * Math.PI / 180));
    const coords: [number, number][] = [];
    for (let i = 0; i <= points; i++) {
      const angle = (i / points) * 2 * Math.PI;
      const dLat = Math.sin(angle) * radiusKm * latDegPerKm;
      const dLng = Math.cos(angle) * radiusKm * lngDegPerKm;
      coords.push([lng + dLng, lat + dLat]);
    }
    return {
      type: 'Feature',
      properties: {},
      geometry: { type: 'Polygon', coordinates: [coords] },
    };
  }

  selectStation(station: PickpointStation) {
    this.selected.set(station);
  }

  confirm() {
    const s = this.selected();
    if (!s) return;
    this.stationSelected.emit(s);
    this.closed.emit();
  }

  cancel() {
    this.closed.emit();
  }
}
